import { AddressType } from '~/types/Address';
import { InsuranceSettings } from '~/types/Insurance';
import { OrderSelection, OrderObjectId } from '~/types/Order';
import { TransactionStatus } from '~/types/Transaction';

type TrackingEventObject = {
  name: string;
  operations: Record<string, string>;
};

const TRACKING_OPERATIONS = {
  ADDRESS_ADDED: 'added',
  ADDRESS_CREATED: 'created',
  ADDRESS_DELETED: 'deleted',
  ADDRESS_SAVE_ATTEMPTED: 'save attempted',
  ADDRESS_UPDATED: 'updated',
  ADDRESS_VIEWED: 'viewed',

  CARRIER_ACCOUNT_STARTED: 'started',
  CARRIER_ACCOUNT_ADDED: 'added',
  CARRIER_ACCOUNT_SELECTED: 'selected',
  CARRIER_ACCOUNT_SAVE_ATTEMPTED: 'save attempted',
  CARRIER_ACCOUNT_CLOSED: 'closed',
  CARRIER_ACCOUNT_ACTIVATED: 'activated',
  CARRIER_ACCOUNT_DEACTIVATED: 'deactivated',
  CARRIER_ACCOUNT_EDITED: 'edited',

  DEFAULT_UPDATED_LABEL: 'label',
  DEFAULT_UPDATED_INTERNATIONAL: 'international',

  LABEL_SAVE_ATTEMPTED: 'save attempted',
  LABEL_TRANSACTION_COMPLETED: 'transaction completed',

  LABEL_DOWNLOADED: 'downloaded',

  ORDER_ADDED: 'added',
  ORDER_UPDATED: 'updated',
  ORDER_IMPORT_STARTED: 'import started',
  ORDER_VIEWED: 'viewed',
  ORDER_DESELECTED: 'deselected',
  ORDER_DUPLICATED: 'duplicated',

  PACKAGE_CREATED: 'created',
  PACKAGE_SAVE_ATTEMPTED: 'save attempted',
  PACKAGE_DELETED: 'deleted',
  PACKAGE_BROWSED: 'browsed',
  PACKAGE_SEARCHED: 'searched',
  PACKAGE_ADDED: 'added',
  PACKAGE_UPDATED: 'updated',
  PAYMENT_METHOD_VIEWED: 'viewed',
  PAYMENT_METHOD_SAVE_ATTEMPTED: 'save attempted',
  PAYMENT_METHOD_ADDED: 'added',
  PAYMENT_METHOD_UPDATED: 'updated',

  RATE_SELECTED: 'selected',
  RATE_GENERATED: 'generated',
  RATE_DISPLAYED: 'displayed',

  ITEM_VIEWED: 'viewed',
  ITEM_ADDED: 'added',
  ITEM_EDITED: 'edited',
  ITEM_DELETED: 'deleted',
  ITEM_SAVED: 'saved',

  MANIFEST_UPDATED: 'updated',
  MANIFEST_GENERATED: 'generated',
  MANIFEST_DOWNLOADED: 'downloaded',

  PARCEL_BOT_LOADED: 'loaded',
  PARCEL_BOT_CLOSED: 'closed',
  PARCEL_BOT_QUERIED: 'queried',
  PARCEL_BOT_BACK: 'back',
  PARCEL_BOT_RETURNED: 'returned',

  BULK_BAR_VIEWED: 'viewed',
  BULK_BAR_SELECTED: 'selected',
  BULK_BAR_COMPLETED: 'completed',

  IBD_MODAL_LOADED: 'loaded',
  IBD_MODAL_ACCCEPT: 'accept',
  IBD_MODAL_CLOSED: 'closed',
  IBD_MODAL_CLICKED_OUT: 'clicked out',
  IBD_MODAL_CHECKED: 'checkbox',
  IBD_MODAL_TC: 'TC',
  IBD_MODAL_LEARN_MORE: 'Learn More',

  ADDRESS_BOOK_CREATED: 'created',
  ADDRESS_BOOK_EDITED: 'edited',
  ADDRESS_BOOK_SAVE_ATTEMPTED: 'save attempted',
  ADDRESS_BOOK_QUERIED: 'queried',
  ADDRESS_BOOK_SELECTED: 'selected',
  ADDRESS_BOOK_DELETED: 'deleted',

  API_TOKEN_GENERATED: 'generated',
  API_TOKEN_DELETED: 'deleted',
  API_TOKEN_COPIED: 'copied',
} as const;

export const ADDRESS_TRACKING_EVENT = {
  name: 'Address Updated',
  operations: {
    ADDED: TRACKING_OPERATIONS.ADDRESS_ADDED,
    CREATED: TRACKING_OPERATIONS.ADDRESS_CREATED,
    UPDATED: TRACKING_OPERATIONS.ADDRESS_UPDATED,
    SAVE_ATTEMPTED: TRACKING_OPERATIONS.ADDRESS_SAVE_ATTEMPTED,
    VIEWED: TRACKING_OPERATIONS.ADDRESS_VIEWED,
    DELETED: TRACKING_OPERATIONS.ADDRESS_DELETED,
  },
} as const satisfies TrackingEventObject;

export type AddressTrackingEventType = typeof ADDRESS_TRACKING_EVENT;

export const CARRIER_ACCOUNT_TRACKING_EVENT = {
  name: 'Carrier Account Updated',
  operations: {
    ADDED: TRACKING_OPERATIONS.CARRIER_ACCOUNT_ADDED,
    STARTED: TRACKING_OPERATIONS.CARRIER_ACCOUNT_STARTED,
    SELECTED: TRACKING_OPERATIONS.CARRIER_ACCOUNT_SELECTED,
    SAVE_ATTEMPTED: TRACKING_OPERATIONS.CARRIER_ACCOUNT_SAVE_ATTEMPTED,
    CLOSED: TRACKING_OPERATIONS.CARRIER_ACCOUNT_CLOSED,
    ACTIVATED: TRACKING_OPERATIONS.CARRIER_ACCOUNT_ACTIVATED,
    DEACTIVATED: TRACKING_OPERATIONS.CARRIER_ACCOUNT_DEACTIVATED,
    EDITED: TRACKING_OPERATIONS.CARRIER_ACCOUNT_EDITED,
  },
} as const satisfies TrackingEventObject;

export type CarrierAccountTrackingEventType = typeof CARRIER_ACCOUNT_TRACKING_EVENT;

export const DEFAULT_UPDATED_EVENT = {
  name: 'Default Updated',
  operations: {
    LABEL: TRACKING_OPERATIONS.DEFAULT_UPDATED_LABEL,
    INTERNATIONAL: TRACKING_OPERATIONS.DEFAULT_UPDATED_INTERNATIONAL,
  },
} as const satisfies TrackingEventObject;

export const ORDER_TRACKING_EVENT = {
  name: 'Order Updated',
  operations: {
    ADDED: TRACKING_OPERATIONS.ORDER_ADDED,
    UPDATED: TRACKING_OPERATIONS.ORDER_UPDATED,
    IMPORT_STARTED: TRACKING_OPERATIONS.ORDER_IMPORT_STARTED,
    VIEWED: TRACKING_OPERATIONS.ORDER_VIEWED,
    DESELECTED: TRACKING_OPERATIONS.ORDER_DESELECTED,
    DUPLICATED: TRACKING_OPERATIONS.ORDER_DUPLICATED,
  },
} as const satisfies TrackingEventObject;

export type OrderTrackingEventType = typeof ORDER_TRACKING_EVENT;

export const LABEL_PURCHASED_EVENT = {
  name: 'Label Purchased',
  operations: {
    SAVE_ATTEMPTED: TRACKING_OPERATIONS.LABEL_SAVE_ATTEMPTED,
    TRANSACTION_COMPLETED: TRACKING_OPERATIONS.LABEL_TRANSACTION_COMPLETED,
  },
} as const satisfies TrackingEventObject;

export type LabelPurchasedEventType = typeof LABEL_PURCHASED_EVENT;

export const LABEL_UPDATED_EVENT = {
  name: 'Label Updated',
  operations: {
    DOWNLOADED: TRACKING_OPERATIONS.LABEL_DOWNLOADED,
  },
} as const satisfies TrackingEventObject;

export type LabelUpdatedEventType = typeof LABEL_UPDATED_EVENT;

export const PACKAGE_TRACKING_EVENT = {
  name: 'Package Updated',
  operations: {
    CREATED: TRACKING_OPERATIONS.PACKAGE_CREATED,
    SAVE_ATTEMPTED: TRACKING_OPERATIONS.PACKAGE_SAVE_ATTEMPTED,
    DELETED: TRACKING_OPERATIONS.PACKAGE_DELETED,
    BROWSED: TRACKING_OPERATIONS.PACKAGE_BROWSED,
    SEARCHED: TRACKING_OPERATIONS.PACKAGE_SEARCHED,
    ADDED: TRACKING_OPERATIONS.PACKAGE_ADDED,
    UPDATED: TRACKING_OPERATIONS.PACKAGE_UPDATED,
  },
} as const satisfies TrackingEventObject;

export type PackageTrackingEventType = typeof PACKAGE_TRACKING_EVENT;

export const PAYMENT_METHOD_EVENT = {
  name: 'Payment Method Updated',
  operations: {
    VIEWED: TRACKING_OPERATIONS.PAYMENT_METHOD_VIEWED,
    ADDED: TRACKING_OPERATIONS.PAYMENT_METHOD_ADDED,
    SAVE_ATTEMPTED: TRACKING_OPERATIONS.PAYMENT_METHOD_SAVE_ATTEMPTED,
    UPDATED: TRACKING_OPERATIONS.PAYMENT_METHOD_UPDATED,
  },
} as const satisfies TrackingEventObject;

export type PaymentMethodUpdatedEventType = typeof PAYMENT_METHOD_EVENT;

export const RATES_UPDATED_EVENT = {
  name: 'Rates Updated',
  operations: {
    SELECTED: TRACKING_OPERATIONS.RATE_SELECTED,
    GENERATED: TRACKING_OPERATIONS.RATE_GENERATED,
  },
} as const satisfies TrackingEventObject;

export type RatesUpdatedEventType = typeof RATES_UPDATED_EVENT;

export const RATE_DISPLAYED_EVENT = {
  name: 'Rate Displayed',
  operations: {
    DISPLAYED: TRACKING_OPERATIONS.RATE_DISPLAYED,
  },
} as const satisfies TrackingEventObject;

export type RateDisplayedEventType = typeof RATE_DISPLAYED_EVENT;

export const ITEM_UPDATED_EVENT = {
  name: 'Item Updated',
  operations: {
    VIWED: TRACKING_OPERATIONS.ITEM_VIEWED,
    ADDED: TRACKING_OPERATIONS.ITEM_ADDED,
    EDITED: TRACKING_OPERATIONS.ITEM_EDITED,
    DELETED: TRACKING_OPERATIONS.ITEM_DELETED,
    SAVED: TRACKING_OPERATIONS.ITEM_SAVED,
  },
} as const satisfies TrackingEventObject;

export const MANIFEST_TRACKING_EVENT = {
  name: 'Manifest Updated',
  operations: {
    UPDATED: TRACKING_OPERATIONS.MANIFEST_UPDATED,
    GENERATED: TRACKING_OPERATIONS.MANIFEST_GENERATED,
    DOWNLOADED: TRACKING_OPERATIONS.MANIFEST_DOWNLOADED,
  },
} as const satisfies TrackingEventObject;

export const PARCEL_BOT_TRACKING_EVENT = {
  name: 'Parcel Bot Updated',
  operations: {
    LOADED: TRACKING_OPERATIONS.PARCEL_BOT_LOADED,
    CLOSED: TRACKING_OPERATIONS.PARCEL_BOT_CLOSED,
    QUERIED: TRACKING_OPERATIONS.PARCEL_BOT_QUERIED,
    BACK: TRACKING_OPERATIONS.PARCEL_BOT_BACK,
    RETURNED: TRACKING_OPERATIONS.PARCEL_BOT_RETURNED,
  },
} as const satisfies TrackingEventObject;

export type ParcelBotTrackingEventType = typeof PARCEL_BOT_TRACKING_EVENT;

export const BULK_BAR_TRACKING_EVENT = {
  name: 'Bulk Bar Updated',
  operations: {
    VIEWED: TRACKING_OPERATIONS.BULK_BAR_VIEWED,
    SELECTED: TRACKING_OPERATIONS.BULK_BAR_SELECTED,
    COMPLETED: TRACKING_OPERATIONS.BULK_BAR_COMPLETED,
  },
} as const satisfies TrackingEventObject;
export type BulkBarTrackingEventType = typeof BULK_BAR_TRACKING_EVENT;

export const IBD_MODAL_TRACKING_EVENT = {
  name: 'IBD Modal Loaded',
  operations: {
    LOADED: TRACKING_OPERATIONS.IBD_MODAL_LOADED,
    ACCEPT: TRACKING_OPERATIONS.IBD_MODAL_ACCCEPT,
    CLOSED: TRACKING_OPERATIONS.IBD_MODAL_CLOSED,
    CLICKED_OUT: TRACKING_OPERATIONS.IBD_MODAL_CLICKED_OUT,
    CHECKBOX: TRACKING_OPERATIONS.IBD_MODAL_CHECKED,
    TC: TRACKING_OPERATIONS.IBD_MODAL_TC,
    LEARN_MORE: TRACKING_OPERATIONS.IBD_MODAL_LEARN_MORE,
  },
} as const satisfies TrackingEventObject;
export type IbdModalTrackingEventType = typeof IBD_MODAL_TRACKING_EVENT;

export const RECIPIENT_ADDRESS_BOOK_UPDATED = {
  name: 'Recipient Address Book Updated',
  operations: {
    CREATED: TRACKING_OPERATIONS.ADDRESS_BOOK_CREATED,
    EDITED: TRACKING_OPERATIONS.ADDRESS_BOOK_EDITED,
    SAVE_ATTEMPTED: TRACKING_OPERATIONS.ADDRESS_BOOK_SAVE_ATTEMPTED,
    DELETED: TRACKING_OPERATIONS.ADDRESS_BOOK_DELETED,
  },
} as const satisfies TrackingEventObject;
export type RecipientAddressBookUpdatedType = typeof RECIPIENT_ADDRESS_BOOK_UPDATED;

export const RECIPIENT_ADDRESS_BOOK_QUERIED = {
  name: 'Recipient Address Book Queried',
  operations: {
    QUERIED: TRACKING_OPERATIONS.ADDRESS_BOOK_QUERIED,
    SELECTED: TRACKING_OPERATIONS.ADDRESS_BOOK_SELECTED,
  },
} as const satisfies TrackingEventObject;
export type RecipientAddressBookQueriedType = typeof RECIPIENT_ADDRESS_BOOK_QUERIED;

export const API_TOKEN_UPDATED = {
  name: 'Token Updated',
  operations: {
    GENERATED: TRACKING_OPERATIONS.API_TOKEN_GENERATED,
    DELETED: TRACKING_OPERATIONS.API_TOKEN_DELETED,
    COPIED: TRACKING_OPERATIONS.API_TOKEN_COPIED,
  },
} as const satisfies TrackingEventObject;
export type APITokenUpdatedType = typeof API_TOKEN_UPDATED;

export const TRACKING_EVENTS = {
  [ADDRESS_TRACKING_EVENT.name]: ADDRESS_TRACKING_EVENT,
  [DEFAULT_UPDATED_EVENT.name]: DEFAULT_UPDATED_EVENT,
  [ORDER_TRACKING_EVENT.name]: ORDER_TRACKING_EVENT,
  [LABEL_PURCHASED_EVENT.name]: LABEL_PURCHASED_EVENT,
  [LABEL_UPDATED_EVENT.name]: LABEL_UPDATED_EVENT,
  [PACKAGE_TRACKING_EVENT.name]: PACKAGE_TRACKING_EVENT,
  [PAYMENT_METHOD_EVENT.name]: PAYMENT_METHOD_EVENT,
  [CARRIER_ACCOUNT_TRACKING_EVENT.name]: CARRIER_ACCOUNT_TRACKING_EVENT,
  [RATES_UPDATED_EVENT.name]: RATES_UPDATED_EVENT,
  [RATE_DISPLAYED_EVENT.name]: RATE_DISPLAYED_EVENT,
  [ITEM_UPDATED_EVENT.name]: ITEM_UPDATED_EVENT,
  [MANIFEST_TRACKING_EVENT.name]: MANIFEST_TRACKING_EVENT,
  [PARCEL_BOT_TRACKING_EVENT.name]: PARCEL_BOT_TRACKING_EVENT,
  [BULK_BAR_TRACKING_EVENT.name]: BULK_BAR_TRACKING_EVENT,
  [IBD_MODAL_TRACKING_EVENT.name]: IBD_MODAL_TRACKING_EVENT,
  [RECIPIENT_ADDRESS_BOOK_UPDATED.name]: RECIPIENT_ADDRESS_BOOK_UPDATED,
  [RECIPIENT_ADDRESS_BOOK_QUERIED.name]: RECIPIENT_ADDRESS_BOOK_QUERIED,
  [API_TOKEN_UPDATED.name]: API_TOKEN_UPDATED,
} as const satisfies Record<string, TrackingEventObject>;

export type TrackingEventName = keyof typeof TRACKING_EVENTS;

export type RequiredTrackingProps = {
  [ADDRESS_TRACKING_EVENT.name]: {
    addressId: number | undefined;
    addressType: AddressType | undefined;
  };
  [DEFAULT_UPDATED_EVENT.name]: Record<string, never>;
  [ORDER_TRACKING_EVENT.name]: Record<string, never>;
  [LABEL_PURCHASED_EVENT.name]: Record<string, never>;
  [LABEL_UPDATED_EVENT.name]: {
    type: LabelDownloadedTrackingType | undefined;
  };
  [PACKAGE_TRACKING_EVENT.name]: {
    parcelId: string | undefined;
    isAutoSave: boolean | undefined;
    isQuickSelect: boolean | undefined;
  };
  [PAYMENT_METHOD_EVENT.name]: Record<string, never>; // TODO: Do we need a more specific location?
  [CARRIER_ACCOUNT_TRACKING_EVENT.name]: {
    carrierName: string | undefined;
    carrierAccountId: string | undefined;
    carrierType: 'BYOA' | 'Shippo Master' | undefined;
  };
  [RATES_UPDATED_EVENT.name]: {
    rateIds: string[] | undefined;
  };
  [RATE_DISPLAYED_EVENT.name]: Record<string, never>;
  [ITEM_UPDATED_EVENT.name]: Record<string, never>;
  [MANIFEST_TRACKING_EVENT.name]: Record<string, never>;
  [IBD_MODAL_TRACKING_EVENT.name]: Record<string, never>;
  [PARCEL_BOT_TRACKING_EVENT.name]: {
    is_error: boolean | undefined;
    query_id: string | undefined;
    query: string | undefined;
    answer_feedback: '-1' | '+1' | undefined;
    answer_feedback_query_id: string | undefined;
  };
  [BULK_BAR_TRACKING_EVENT.name]: {
    action:
      | 'bulk_buy'
      | 'show_bulk_bar'
      | 'edit_address'
      | 'edit_rates'
      | 'edit_package'
      | 'view_items'
      | 'edit_insurance_and_signature'
      | 'view_selection_only'
      | 'copy_tracking'
      | 'add_packing_slip_note'
      | 'download_packing_slip'
      | 'hide_order'
      | 'hide_shipment';
    orders_selected: number | undefined;
    fulfillment_type: 'fulfilled' | 'unfulfilled';
  };
  [RECIPIENT_ADDRESS_BOOK_UPDATED.name]: Record<string, never>;
  [RECIPIENT_ADDRESS_BOOK_QUERIED.name]: Record<string, never>;

  [API_TOKEN_UPDATED.name]: {
    tokenId: string;
    environment: 'test' | 'prod';
    isMaximum: boolean;
  };
};

export type TrackingAppLocation =
  | 'legacy_mobile_orders'
  | 'side_panel'
  | 'order_details'
  | 'order_table'
  | 'shipments'
  | 'settings'
  | 'onboarding'
  | 'rates_at_checkout'
  | 'automations'
  | 'manifests'
  | 'help'
  | 'other'
  | 'bulk_bar'
  | 'download';

export interface TrackingReference {
  appLocation: TrackingAppLocation;
  appVersionName: 'classic' | 'blackbeard';
  initialEventName: TrackingEventName;
  isBulk?: boolean;
  orderQuantity?: number;
  orderId?: OrderObjectId;
  orderIds?: OrderObjectId[];
  userDetails?: UserDetails;
  trackedOrderDetailsByShipment?: Record<number, TrackedOrderDetails>;
  shipmentId?: number;
  selectedOrders?: OrderSelection;
  referenceId: string;
}

// Use this to inject TrackingAppLocation into component prop type definitions
export interface TrackingAppProps {
  appLocation: TrackingAppLocation;
}

export type ExtendedTrackingReference<E extends TrackingEventName> = TrackingReference & {
  initialEventName: E;
} & RequiredTrackingProps[E];

export type OperationsForEvent<E extends TrackingEventName> =
  (typeof TRACKING_EVENTS)[E]['operations'][keyof (typeof TRACKING_EVENTS)[E]['operations']];

type ManifestTrackingProps = {
  carrier: string;
  carrierAccountId: string;
  manifestLabelCount: number;
  senderAddressId: number;
  // ISO 8601
  submissionDate: string;
};

export type RequiredOperationProps = {
  [DEFAULT_UPDATED_EVENT.name]: {
    [DEFAULT_UPDATED_EVENT.operations.LABEL]: DefaultLabelTrackedProps;
    [DEFAULT_UPDATED_EVENT.operations.INTERNATIONAL]: InternationalDefaultsTrackedProps;
  };
  [LABEL_PURCHASED_EVENT.name]: {
    [LABEL_PURCHASED_EVENT.operations.SAVE_ATTEMPTED]: {
      rateIds: string[];
    };
    [LABEL_PURCHASED_EVENT.operations.TRANSACTION_COMPLETED]: {
      transactionIds: string[];
      rateIds: string[];
      status: TransactionStatus | 'mixed' | 'none';
    };
  };
  [ITEM_UPDATED_EVENT.name]: {
    [ITEM_UPDATED_EVENT.operations.VIWED]: {
      itemId: string | undefined;
    };
    [ITEM_UPDATED_EVENT.operations.EDITED]: {
      itemId: string | undefined;
    };
    [ITEM_UPDATED_EVENT.operations.DELETED]: {
      itemId: string | undefined;
    };
  };
  [MANIFEST_TRACKING_EVENT.name]: {
    [MANIFEST_TRACKING_EVENT.operations.GENERATED]: ManifestTrackingProps;
    [MANIFEST_TRACKING_EVENT.operations.UPDATED]: ManifestTrackingProps;
    [MANIFEST_TRACKING_EVENT.operations.DOWNLOADED]: {
      carrier: string;
      manifestId: number;
    };
  };
};

export interface DefaultLabelTrackedProps {
  printOrder?: string;
  printLabelFormat?: string;
  printPackingSlipFormat?: string;
  packingSlipTextOnLabel?: string;
  packingSlipIncludeOrderNumber?: boolean;
  packingSlipStorePolicy?: string;
  packingSlipIncludePricing?: boolean;
  outboundDefaultServiceLevel1?: string;
  outboundDefaultServiceLevel2?: string;
  outboundDefaultServiceLevelIsTest?: boolean;
  outboundDefaultServiceLevelIsMasterAccount?: boolean;
  returnDefaultServiceLevel1?: string;
  returnDefaultServiceLevel2?: string;
  returnDefaultServiceLevel2IsTest?: boolean;
  returnDefaultServiceLevel2IsMasterAccount?: boolean;
  returnAutomaticOn?: boolean;
}

export interface InternationalDefaultsTrackedProps {
  incotermDefault?: string;
  shipmentPurposeDefault?: string;
  taxIdDefault?: boolean;
}

export interface TrackedServiceLevelInfo {
  carrier: string;
  serviceLevel: string;
  isTest: boolean;
  isMasterAccount: boolean;
}

export type LabelDownloadedTrackingType = 'packing_slip' | 'shipping_label' | 'both';

export type RequiredPropsForOperation<
  E extends TrackingEventName,
  O extends OperationsForEvent<E>,
> = E extends keyof RequiredOperationProps
  ? O extends keyof RequiredOperationProps[E]
    ? RequiredOperationProps[E][O]
    : never
  : never;

export type UserDetails = {
  defaultSenderAddress?: number;
  defaultReturnAddress?: number;
  defaultParcelTemplate?: string;
  paymentType: string;
  defaultInsuranceSetings?: InsuranceSettings;
};

export type TrackedOrderDetails = {
  addressSender?: string;
  addressSenderDefaultSet?: boolean;
  addressReturn?: string;
  addressReturnDefaultSet?: boolean;
  addressRecipient?: string;
  parcel?: string;
  parcelDefaultSet?: boolean;
  parcelUserTemplate?: string;
  parcelCarrierTemplate?: string;
  return?: string;
  hasReturnOption?: boolean;
  signature?: boolean;
  hasSignatureOption?: boolean;
  carrier?: string;
  serviceLevel?: string;
  hasInsuranceOption?: boolean;
  paymentMethod?: string;
  returnDefaultSet?: boolean;
  paymentMethodDefaultSet?: boolean;
  insuranceDefaultSet?: boolean;
  serviceLevelDefault?: string;
};

export interface TrackedServiceLevelInfo {
  carrier: string;
  serviceLevel: string;
  isTest: boolean;
  isMasterAccount: boolean;
}

export type AllTrackedProps = TrackingReference & DefaultLabelTrackedProps;
